import { AppConstants } from '@/app.constants';
import { ensureDB } from '@/store/idbStorage/idbStorage';
import { SyncLogLevel } from '@/utils/diagnostics/diagnostics.constants';
import { ChunkedLogPart, LogRequestPayload } from '@/utils/diagnostics/logChunker.util';

const OPEN_SYNC_LOGS_STORE = AppConstants.INDEX_DB.store.openSyncLogs;
const PENDING_LOGS_STORE = AppConstants.INDEX_DB.store.pendingLogs;

export interface OpenSyncLogRecord {
  inspectionId: string;
  syncId: string;
  sessionId: string;
  version: string;
  startedAt: string;
  updatedAt: string;
  hasError: boolean;
  droppedEventCount: number;
  byteSize: number;
  events: Array<{
    level: SyncLogLevel;
    message: string;
    data: unknown;
    at: string;
  }>;
}

export interface PendingLogRecord {
  partId: string;
  logId: string;
  sessionId: string;
  syncId: string;
  inspectionId?: string;
  payload: LogRequestPayload;
  byteSize: number;
  createdAt: string;
  attemptCount: number;
  nextAttemptAt: string;
  leaseUntil: string | null;
  level: SyncLogLevel;
}

export const diagnosticsLogStorage = {
  async upsertOpenSyncLog(record: OpenSyncLogRecord): Promise<void> {
    const db = await ensureDB();
    await db.put(OPEN_SYNC_LOGS_STORE, record);
  },

  async getOpenSyncLog(inspectionId: string): Promise<OpenSyncLogRecord | null> {
    const db = await ensureDB();
    return (await db.get(OPEN_SYNC_LOGS_STORE, inspectionId)) ?? null;
  },

  async getAllOpenSyncLogs(): Promise<OpenSyncLogRecord[]> {
    const db = await ensureDB();
    return db.getAll(OPEN_SYNC_LOGS_STORE);
  },

  async deleteOpenSyncLog(inspectionId: string): Promise<void> {
    const db = await ensureDB();
    await db.delete(OPEN_SYNC_LOGS_STORE, inspectionId);
  },

  async handoffToPendingLogs(
    inspectionId: string,
    parts: ChunkedLogPart[],
    meta: { sessionId: string; syncId: string }
  ): Promise<void> {
    const db = await ensureDB();
    const tx = db.transaction([OPEN_SYNC_LOGS_STORE, PENDING_LOGS_STORE], 'readwrite');
    const now = new Date().toISOString();
    for (const part of parts) {
      const record: PendingLogRecord = {
        partId: part.partId,
        logId: part.logId,
        sessionId: meta.sessionId,
        syncId: meta.syncId,
        inspectionId,
        payload: part.payload,
        byteSize: part.byteSize,
        createdAt: now,
        attemptCount: 0,
        nextAttemptAt: now,
        leaseUntil: null,
        level: part.level,
      };
      await tx.objectStore(PENDING_LOGS_STORE).put(record);
    }
    await tx.objectStore(OPEN_SYNC_LOGS_STORE).delete(inspectionId);
    await tx.done;
  },

  async enqueuePendingParts(
    parts: ChunkedLogPart[],
    meta: { sessionId: string; syncId: string }
  ): Promise<void> {
    const db = await ensureDB();
    const tx = db.transaction(PENDING_LOGS_STORE, 'readwrite');
    const now = new Date().toISOString();
    for (const part of parts) {
      const record: PendingLogRecord = {
        partId: part.partId,
        logId: part.logId,
        sessionId: meta.sessionId,
        syncId: meta.syncId,
        payload: part.payload,
        byteSize: part.byteSize,
        createdAt: now,
        attemptCount: 0,
        nextAttemptAt: now,
        leaseUntil: null,
        level: part.level,
      };
      await tx.objectStore(PENDING_LOGS_STORE).put(record);
    }
    await tx.done;
  },

  async getAllPendingLogs(): Promise<PendingLogRecord[]> {
    const db = await ensureDB();
    return db.getAll(PENDING_LOGS_STORE);
  },

  async deletePendingLog(partId: string): Promise<void> {
    const db = await ensureDB();
    await db.delete(PENDING_LOGS_STORE, partId);
  },

  async updatePendingLog(record: PendingLogRecord): Promise<void> {
    const db = await ensureDB();
    await db.put(PENDING_LOGS_STORE, record);
  },
};
